import { useContext, useEffect, useState } from 'preact/hooks';
import { SessionContext } from '../app.jsx';
import { get, post, del } from '../api.js';
import { Link } from '../router.js';
import { Tag, PaidTag } from '../ui/Tag.jsx';
import { markPaid, pendingPaid } from '../outbox.js';
import { guideNames, isOver } from './Calendar.jsx';
import { fmtRange, fmtAsOf } from '../../lib/dates.js';

// A contact as a link the phone can act on: tel: for numbers, mailto: for e-mail.
export function Contact({ value }) {
  if (!value) return null;
  const href = value.includes('@') ? `mailto:${value}` : /^\+?[\d\s/().-]{6,}$/.test(value) ? `tel:${value.replace(/[^\d+]/g, '')}` : null;
  return href ? <a href={href} data-g3-mask>{value}</a> : <span data-g3-mask>{value}</span>;
}

// SC-002 Tura i grupa: the tour, its group with paid marks, and the guide's own equipment.
export function DepartureGuide({ params }) {
  const id = Number(params.id);
  const { user, tick } = useContext(SessionContext);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(new Set()); // booking ids whose paid mark waits for a signal

  const load = () => get(`/departures/${id}`).then((r) => { setData(r); setError(null); }, (e) => setError(e.message));
  useEffect(() => { load(); }, [id, tick]);
  useEffect(() => {
    const read = () => pendingPaid().then(setPending, () => {});
    read();
    window.addEventListener('outbox-change', read);
    return () => window.removeEventListener('outbox-change', read);
  }, []);

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      if ((await fn()) !== 'queued') await load();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };
  const signUp = () => act(() => post(`/departures/${id}/guides`, { guideId: user.id }));
  const signOff = () => confirm('Otkazati prijavu?') && act(() => del(`/departures/${id}/guides/${user.id}`));
  const markPaidNow = (b) => act(() => markPaid(b.id));

  if (!data) return error ? <p class="alert" role="alert">{error}</p> : null;
  const { departure: d, bookings, guides, mine, released, takes, people, asOf } = data;
  const own = takes.filter((k) => k.guide.id === user.id);
  const onTour = mine || released;

  return (
    <>
      <h1>{d.tour.name}</h1>
      <p class="sub">{fmtRange(d.start_date, d.end_date)}</p>
      <p>{guideNames(guides) ?? 'Još nema vodiča'}</p>
      <p class="as-of">Preuzeto {fmtAsOf(asOf)}</p>
      <div class="actions head-actions">
        {mine && <button class="btn-quiet" disabled={busy} onClick={signOff}>Otkaži prijavu</button>}
        {!mine && !isOver(d) && <button class="btn" disabled={busy} onClick={signUp}>Prijavi se</button>}
      </div>
      {error && <p class="alert" role="alert">{error}</p>}

      <section class="section group" aria-labelledby="grupa">
        <h2 id="grupa">{`Grupa — ${people} ljudi`}</h2>
        {bookings.length === 0 && <p class="empty">Još nema rezervacija za ovu turu.</p>}
        <ul class="plain-list rows">
          {bookings.map((b) => (
            <li key={b.id} class="booking">
              <div class="booking-main">
                <strong>{b.name}</strong>
                <Contact value={b.contact} />
                <span class="num">{b.people} ljudi</span>
              </div>
              {b.note && <p class="note">{b.note}</p>}
              <div class="booking-paid">
                <PaidTag paid={b.paid} />
                {!b.paid && onTour && (pending.has(b.id)
                  ? <Tag kind="osteceno">Čeka slanje</Tag>
                  : <button class="btn-quiet" disabled={busy} onClick={() => markPaidNow(b)}>Označi plaćeno</button>)}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section class="section" aria-labelledby="moja-oprema">
        <h2 id="moja-oprema">Moja oprema</h2>
        {released && <p class="notice">Popis je sačuvan.</p>}
        {!released && own.length === 0 && <p class="empty">Još nisi upisao opremu.</p>}
        {own.length > 0 && (
          <ul class="plain-list rows">
            {own.map((k) => <li key={k.id}>{`${k.item.name} — ${k.qty}`}</li>)}
          </ul>
        )}
        {mine && (
          <div class="actions">
            <Link href={`/ture/${id}/oprema`} class="btn">Upiši opremu</Link>
            <Link href={`/ture/${id}/popis`} class="btn-quiet">Popis opreme</Link>
          </div>
        )}
      </section>
    </>
  );
}
